import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/query';
import { getEnvironment } from '../util/env';
import {
  endSession,
  getValidAccessToken,
  refreshTokenOnce
} from './auth-token';
import { buildAuthHeaders, type AuthExtraOptions } from './auth-headers';
import { ENTITY_SCOPE_HEADER, refusedForKnownUnreachableSelection } from './entity-scope-header';

/**
 * One raw base query per header policy: the selection header or none.
 * `prepareHeaders` cannot see `extraOptions`, so the choice is made by picking
 * the instance (DV-3421 review round 6, item H).
 */
const makeRawBaseQuery = (withEntityScope: boolean) =>
  fetchBaseQuery({
    baseUrl: getEnvironment(true),
    prepareHeaders: async (headers) => {
      let token: string | null = null;
      try {
        token = await getValidAccessToken();
      } catch {
        // Refresh attempt failed — let the request go without auth; the
        // response handler below catches the resulting 401/403 and ends
        // the session.
      }

      // BC-7. Outside the try above deliberately: a failed token refresh must
      // not also drop the scope assertion, and this read cannot throw. Both
      // headers come from the same token so they cannot disagree (DV-3421).
      Object.entries(buildAuthHeaders(token, { withEntityScope })).forEach(([name, value]) =>
        headers.set(name, value),
      );
      return headers;
    },
  });

const scopedBaseQuery = makeRawBaseQuery(true);
const tokenOnlyBaseQuery = makeRawBaseQuery(false);

/**
 * Shared RTK Query base query with proactive silent refresh + 401/403 retry.
 * Mirrors src/services/axios-interceptor.ts via the shared auth-token module
 * so axios and RTK Query share one refresh lock. That lock is about efficiency
 * and ordering, not survival: refresh tokens are REUSABLE — realm `planomatic`
 * runs `revokeRefreshToken=false` in QA and prod (measured through the admin
 * API on 13 Oct 2026; `refreshTokenMaxReuse=0` is ignored while revocation is
 * off, and `planomatic-connect-client` has no override). An earlier version of
 * this comment said single-use; it was wrong. If revocation is ever turned on,
 * concurrent refreshes from several tabs lose races — `refreshTokenOnce`
 * adopts the token the winning tab stored rather than failing, so a lost race
 * never reaches the `endSession()` below.
 *
 * `extraOptions.tokenOnly` (an endpoint's own flag) drops `X-Entity-UUID`, and
 * `extraOptions.forbiddenIsFinal` returns a 403 without the refresh — see
 * `AuthExtraOptions`.
 */
export const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  AuthExtraOptions,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const rawBaseQuery = extraOptions?.tokenOnly ? tokenOnlyBaseQuery : scopedBaseQuery;
  const result = await rawBaseQuery(args, api, extraOptions);

  if (
    result.error &&
    (result.error.status === 401 || result.error.status === 403)
  ) {
    if (
      result.error.status === 403 &&
      (extraOptions?.forbiddenIsFinal ||
        refusedForKnownUnreachableSelection(result.meta?.request.headers.get(ENTITY_SCOPE_HEADER)))
    ) {
      return result;
    }
    try {
      await refreshTokenOnce();
    } catch {
      endSession();
      return result;
    }
    const retryResult = await rawBaseQuery(args, api, extraOptions);
    if (retryResult.error && retryResult.error.status === 401) {
      // Refresh succeeded but the retried request STILL got a 401 — the
      // session is genuinely invalid (e.g. revoked server-side). End cleanly.
      //
      // A repeated 403 is deliberately NOT treated this way: the refresh just
      // proved the session is valid, so a persisting 403 is the server
      // refusing this *specific* request — an authorization decision or a
      // CORS/method rejection (e.g. a backend whose CORS config omits the
      // verb). Logging the user out of a valid session on those is wrong;
      // let the 403 surface so the caller can roll back / show an error.
      endSession();
    }
    return retryResult;
  }

  return result;
};

/**
 * True when an RTK Query error represents a 4xx response (client-side: bad
 * UUID, resource not found, forbidden, etc). Used by detail-page hooks to
 * distinguish "this resource is unavailable to you — show not-found/forbidden"
 * from "the server failed — show retry." A 401 never reaches consumers (the
 * baseQuery refreshes and, if still unauthenticated, ends the session). A 403
 * that persists after a successful refresh DOES surface here, so consumers
 * treat it as a normal 4xx (forbidden) rather than a session death.
 *
 * Accepts `unknown` because consumers in .unwrap().catch() blocks receive
 * the rejected error without RTK Query's typed query-state wrapper.
 */
export function is4xxError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' && status >= 400 && status < 500;
}
